import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, ArrowDown } from "lucide-react";
import { useSEO } from "../components/SEO";
import { Reveal, MaskedLines } from "../components/AnimatedSection";
import { Button } from "../components/Button";
import { Modal } from "../components/Modal";
import { projects } from "../data/projects";

const Section = ({ no, title, children, dark = false }) => (
  <Reveal>
    <div className="border-t border-[#07130C]/10 py-12 md:py-16">
      <div className="grid gap-8 lg:grid-cols-[220px_1fr]">
        <div>
          <p className="text-gradient-pulse font-display text-sm font-bold">{no}</p>
          <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight text-[#07130C] md:text-3xl">
            {title}
          </h2>
        </div>
        <div className="text-base leading-relaxed text-[#07130C]/65">{children}</div>
      </div>
    </div>
  </Reveal>
);

const CaseStudy = () => {
  const { slug } = useParams();
  const project = projects.find((p) => p.slug === slug);
  const [lightbox, setLightbox] = useState(null);

  useSEO(
    project
      ? `${project.name} — Case Study | Social Wavez Technologies`
      : "Project | Social Wavez Technologies",
    project?.description,
  );

  if (!project) {
    return (
      <section className="bg-deep flex min-h-screen flex-col items-center justify-center px-6 text-center">
        <h1 className="font-display text-4xl font-semibold text-white">Project not found</h1>
        <p className="mt-4 text-white/55">This case study doesn't exist (yet).</p>
        <div className="mt-8">
          <Button to="/project" data-testid="case-study-back-missing">
            <ArrowLeft className="h-4 w-4" /> Back to Projects
          </Button>
        </div>
      </section>
    );
  }

  const cs = project.caseStudy;

  return (
    <>
      {/* Hero */}
      <section className="bg-deep relative overflow-hidden pb-20 pt-36 md:pt-48">
        <div className="bg-grid-dark absolute inset-0 opacity-50" />
        <div className="relative mx-auto max-w-7xl px-6 md:px-12">
          <Reveal>
            <Link
              to="/project"
              data-testid="case-study-back"
              className="inline-flex items-center gap-2 text-sm font-medium text-white/55 transition-colors hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" /> All Projects
            </Link>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 text-[11px] font-semibold uppercase tracking-[0.3em] text-[#D3B74D]">
              {project.category} · {project.industry}
            </p>
          </Reveal>
          <h1 className="mt-5 font-display text-4xl font-semibold tracking-tight text-white sm:text-5xl lg:text-6xl">
            <MaskedLines trigger="mount" delay={0.25} lines={[project.name]} />
          </h1>
          <Reveal delay={0.4}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/60 md:text-lg">
              {project.description}
            </p>
          </Reveal>
          <Reveal delay={0.5}>
            <div className="mt-8 flex flex-wrap gap-2">
              {project.tech.map((t) => (
                <span key={t} className="rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-medium text-white/75">
                  {t}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.6}>
            <div className="relative mt-14 overflow-hidden rounded-3xl border border-white/10 shadow-[0_40px_120px_rgba(0,0,0,0.5)]">
              <img
                src={project.image}
                alt={`${project.name} hero visual`}
                className="aspect-[16/8] w-full object-cover"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#07130C]/70 via-transparent to-transparent" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Body */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-5xl px-6 md:px-12">
          <Section no="01" title="The Client Problem">
            <p>{cs.problem}</p>
          </Section>

          <Section no="02" title="Objectives">
            <ul className="space-y-3">
              {cs.objectives.map((o) => (
                <li key={o} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#1C7543]" />
                  <span>{o}</span>
                </li>
              ))}
            </ul>
          </Section>

          <Section no="03" title="The Solution">
            <p>{cs.solution}</p>
          </Section>

          <Section no="04" title="Key Features">
            <div className="grid gap-3 sm:grid-cols-2">
              {cs.features.map((f) => (
                <div key={f} className="rounded-2xl border border-[#07130C]/10 bg-[#F8FBF6] px-5 py-4 text-sm font-medium text-[#07130C]/75">
                  {f}
                </div>
              ))}
            </div>
          </Section>

          <Section no="05" title="UI Screens">
            <div className="grid gap-5 sm:grid-cols-2">
              {cs.screens.map((src, i) => (
                <button
                  key={src}
                  type="button"
                  data-testid={`screen-thumb-${i}`}
                  onClick={() => setLightbox(src)}
                  className="group overflow-hidden rounded-2xl border border-[#07130C]/10 text-left"
                >
                  <img
                    src={src}
                    alt={`${project.name} interface screen ${i + 1}`}
                    loading="lazy"
                    className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                  />
                </button>
              ))}
            </div>
            <p className="mt-4 text-xs uppercase tracking-[0.2em] text-[#07130C]/35">
              Interface screens from the live system, shared with client approval
            </p>
          </Section>

          <Section no="06" title="Architecture — High Level">
            <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center">
              {cs.architecture.map((a, i) => (
                <div key={a} className="flex items-center gap-3">
                  {i > 0 && <ArrowRight className="hidden h-4 w-4 text-[#D3B74D] md:block" />}
                  {i > 0 && <ArrowDown className="h-4 w-4 text-[#D3B74D] md:hidden" />}
                  <span className="rounded-full border border-[#1C7543]/25 bg-white px-5 py-2.5 font-display text-sm font-semibold text-[#07130C] shadow-sm">
                    {a}
                  </span>
                </div>
              ))}
            </div>
            <p className="mt-4 text-sm text-[#07130C]/45">
              High-level flow only — implementation details stay confidential.
            </p>
          </Section>

          <Section no="07" title="Technologies Used">
            <div className="flex flex-wrap gap-2">
              {project.tech.map((t) => (
                <span key={t} className="rounded-full bg-gradient-to-r from-[#1C7543]/10 to-[#D3B74D]/10 px-4 py-2 text-sm font-semibold text-[#1C7543]">
                  {t}
                </span>
              ))}
            </div>
          </Section>

          <Section no="08" title="Results">
            <div className="rounded-2xl border border-dashed border-[#1C7543]/35 bg-[#1C7543]/5 p-6">
              <p className="text-sm leading-relaxed text-[#07130C]/60">{cs.results}</p>
            </div>
          </Section>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-deep relative overflow-hidden py-24 md:py-32">
        <div className="bg-grid-dark absolute inset-0 opacity-50" />
        <div className="relative mx-auto max-w-3xl px-6 text-center md:px-12">
          <h2 className="font-display text-3xl font-semibold tracking-tight text-white md:text-5xl">
            Have a Similar Problem?
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-white/55">
            Tell us what's broken, manual or disconnected — we'll map the system
            that fixes it on a free discovery call.
          </p>
          <div className="mt-10">
            <Button to="/contact" data-testid="case-study-cta">
              Book My Free Discovery Call <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      <Modal open={!!lightbox} onClose={() => setLightbox(null)} label={`${project.name} screen preview`}>
        {lightbox && (
          <img
            src={lightbox}
            alt={`${project.name} interface screen — enlarged`}
            className="w-full rounded-2xl border border-white/10"
          />
        )}
      </Modal>
    </>
  );
};

export default CaseStudy;
